import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import TopBar from "./components/TopBar";
import RegionChips, { filterByRegion } from "./components/RegionChips";
import MapView from "./components/MapView";
import MapControls from "./components/MapControls";
import Sheet from "./components/Sheet";
import CafeList from "./components/CafeList";
import CafeDetail from "./components/CafeDetail";
import PlaceDetail from "./components/PlaceDetail";
import SidePanel from "./components/SidePanel";
import { useIsDesktop } from "./lib/useMediaQuery";
import { requestUserLocation, isInJeju, reverseGeocode } from "./lib/naverMap";
import { haversineDistanceM } from "./lib/distance";
import { useCafesIndex, useCafe, usePlace } from "./lib/dataClient";

// 어드민 — dev 모드 + #admin 해시일 때만 동적 로드. production 빌드에선 import 자체가 일어나지 않음.
const AdminApp = import.meta.env.DEV
  ? lazy(() => import("./admin/AdminApp.jsx"))
  : null;

function isAdminRoute() {
  if (typeof window === "undefined") return false;
  return (window.location.hash || "").startsWith("#admin");
}

export default function App() {
  // ── 어드민 라우트 분기 (hook 호출 전, dev only) ────────────
  const [adminMode, setAdminMode] = useState(() =>
    Boolean(AdminApp) && isAdminRoute()
  );
  useEffect(() => {
    if (!AdminApp) return;
    const on = () => setAdminMode(isAdminRoute());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);

  if (AdminApp && adminMode) {
    return (
      <Suspense fallback={<div style={{ padding: 20 }}>관리자 로딩…</div>}>
        <AdminApp />
      </Suspense>
    );
  }

  return <MainApp />;
}

function MainApp() {
  const isDesktop = useIsDesktop();

  // ── 데이터 로딩 (chunk 아키텍처) ─────────────────────────
  // 1) 인덱스 (메타데이터만, ~500KB) — 첫 로드 한 번
  const { data: index, loading: indexLoading, error: indexError } = useCafesIndex();

  // 2) 카페 클릭 시 풀 데이터 (recommends + routing path) 만 lazy fetch
  // 3) 음식점 클릭 시 풀 데이터 (naverDescription 등) lazy fetch
  const [selectedCafeId, setSelectedCafeId] = useState(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const { data: selectedCafeFull } = useCafe(selectedCafeId);
  const { data: selectedPlaceFull } = usePlace(selectedPlaceId);

  // 메타 lookup — 풀 데이터 로딩 전이라도 메타로 즉시 카드 그릴 수 있음
  const indexCafes = index?.cafes ?? [];
  const indexPlaces = index?.places ?? [];

  const selectedCafeMeta = useMemo(
    () => (selectedCafeId ? indexCafes.find((c) => c.id === selectedCafeId) : null),
    [selectedCafeId, indexCafes]
  );
  const selectedPlaceMeta = useMemo(
    () => (selectedPlaceId ? indexPlaces.find((p) => p.id === selectedPlaceId) : null),
    [selectedPlaceId, indexPlaces]
  );

  // 풀 데이터 우선 사용, 없으면 메타로 fallback — UX 연속성 유지
  const selectedCafe = selectedCafeFull || selectedCafeMeta;
  const selectedPlace = selectedPlaceFull || selectedPlaceMeta;

  // ── UI 상태 ──────────────────────────────────────────────
  const [sheetMode, setSheetMode] = useState("peek"); // peek | half | full
  const [routeMode, setRouteMode] = useState("walking"); // walking | driving
  const [region, setRegion] = useState("all");
  const [mapReady, setMapReady] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [userRegionName, setUserRegionName] = useState(null);
  const mapRef = useRef(null);
  const locationRequestedRef = useRef(false);

  const visibleCafes = useMemo(
    () => filterByRegion(indexCafes, region),
    [indexCafes, region]
  );

  // ── 핸들러 ───────────────────────────────────────────────
  const handleSelectCafe = useCallback((cafe) => {
    setSelectedCafeId(cafe.id);
    setSelectedPlaceId(null);
    setSheetMode("half");
  }, []);

  const handleSelectPlace = useCallback((place) => {
    setSelectedPlaceId(place.id);
    setSheetMode("half");
  }, []);

  const handleCloseSheet = useCallback(() => {
    setSelectedCafeId(null);
    setSelectedPlaceId(null);
    setSheetMode("peek");
  }, []);

  const handleClosePlace = useCallback(() => {
    setSelectedPlaceId(null);
    setSheetMode("half");
  }, []);

  // 공유 — 현재 상세 대상
  const handleShareCurrent = useCallback(async () => {
    const target = selectedPlace ?? selectedCafe;
    if (!target) return;
    const isPlace = !!selectedPlace;
    const shareData = {
      title: target.name,
      text: isPlace
        ? `${target.name} — ${selectedCafe?.name} 사장님이 추천하는 맛집`
        : `${target.name} — 제주 카페 사장님이 추천하는 맛집`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(`${shareData.title}\n${shareData.url}`);
        alert("링크가 복사되었어요");
      }
    } catch (err) {
      if (err?.name !== "AbortError") console.warn("[share]", err);
    }
  }, [selectedCafe, selectedPlace]);

  const handleMapReady = useCallback((map) => {
    mapRef.current = map;
    setMapReady(true);
  }, []);

  const handleToggleRoute = useCallback(() => {
    setRouteMode((m) => (m === "walking" ? "driving" : "walking"));
  }, []);

  const handleRegionChange = useCallback(
    (newRegion) => {
      setRegion(newRegion);
      setSelectedCafeId((prev) => {
        if (!prev) return prev;
        const filtered = filterByRegion(indexCafes, newRegion);
        if (!filtered.some((c) => c.id === prev)) {
          setSelectedPlaceId(null);
          setSheetMode("peek");
          return null;
        }
        return prev;
      });
    },
    [indexCafes]
  );

  // userLocation 설정 시 reverse geocoding
  useEffect(() => {
    if (!userLocation || !mapReady) return;
    reverseGeocode(userLocation.lat, userLocation.lng)
      .then(({ regionName }) => setUserRegionName(regionName))
      .catch((err) => {
        console.warn("[reverse-geocode]", err.message);
        setUserRegionName(null);
      });
  }, [userLocation, mapReady]);

  // ── 지도 컨트롤 ──────────────────────────────────────────
  const handleZoomIn = () => {
    const m = mapRef.current;
    if (!m) return;
    m.setZoom(m.getZoom() + 1, true);
  };
  const handleZoomOut = () => {
    const m = mapRef.current;
    if (!m) return;
    m.setZoom(m.getZoom() - 1, true);
  };

  const handleLocate = () => {
    const m = mapRef.current;
    if (!m) return;
    requestUserLocation()
      .then(({ lat, lng }) => {
        if (isInJeju(lat, lng)) {
          setUserLocation({ lat, lng });
          fitToUserAndNearCafes(lat, lng, 3);
        } else {
          console.log("[locate] 현재 위치가 제주도 밖입니다.");
        }
      })
      .catch((err) => console.warn("[locate]", err.message));
  };

  // 내 위치 + 가까운 카페 N개 fit
  const fitToUserAndNearCafes = (lat, lng, count = 3) => {
    const m = mapRef.current;
    if (!m) return;
    const maps = window.naver.maps;

    const nearCafes = indexCafes
      .filter((c) => c.lat != null)
      .map((c) => ({
        ...c,
        _dist: haversineDistanceM(lat, lng, c.lat, c.lng),
      }))
      .sort((a, b) => a._dist - b._dist)
      .slice(0, count);

    const center = new maps.LatLng(lat, lng);

    if (nearCafes.length === 0) {
      m.morph(center, 15, { duration: 600 });
      return;
    }

    const maxDist = nearCafes[nearCafes.length - 1]._dist;
    const targetRadiusM = maxDist * 1.25;

    const sheetReservedPx = isDesktop
      ? 0
      : sheetMode === "full"
      ? window.innerHeight * 0.92
      : sheetMode === "half"
      ? window.innerHeight * 0.5
      : 220;
    const sidePanelPx = isDesktop ? 420 : 0;
    const headerPx = isDesktop ? 0 : 110;
    const viewportW = window.innerWidth - sidePanelPx;
    const viewportH = window.innerHeight - headerPx - sheetReservedPx;
    const shorterPx = Math.max(120, Math.min(viewportW, viewportH));

    const metersPerPixel = (targetRadiusM * 2) / shorterPx;
    const rawZoom = Math.log2(
      (40075016 * Math.cos((lat * Math.PI) / 180)) / (256 * metersPerPixel)
    );
    const zoom = Math.max(10, Math.min(20, Math.round(rawZoom * 10) / 10));

    m.morph(center, zoom, { duration: 600 });
  };

  // ── 페이지 진입 시 자동 현위치 ───────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    if (locationRequestedRef.current) return;
    if (indexCafes.length === 0) return;  // index 로드 전엔 fit 못 함
    locationRequestedRef.current = true;

    requestUserLocation()
      .then(({ lat, lng }) => {
        if (isInJeju(lat, lng)) {
          setUserLocation({ lat, lng });
          fitToUserAndNearCafes(lat, lng, 3);
        }
      })
      .catch((err) => {
        console.log("[location] 자동 위치 요청 실패:", err.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, indexCafes.length]);

  // 시트 모드에 따른 컨트롤 위치
  const mapCtrlsBottom = isDesktop
    ? 24
    : sheetMode === "peek"
    ? 180
    : sheetMode === "half"
    ? "calc(50vh + 20px)"
    : "calc(92vh + 20px)";

  // ── 초기 로딩 / 에러 화면 ────────────────────────────────
  if (indexError) {
    return (
      <div className="cf-app cf-app--loading">
        <div style={{ padding: "40px 20px", textAlign: "center" }}>
          <h2>데이터를 불러올 수 없어요</h2>
          <p style={{ marginTop: 8, color: "#666" }}>
            잠시 후 다시 시도해주세요. ({indexError.message})
          </p>
        </div>
      </div>
    );
  }

  if (indexLoading || !index) {
    return (
      <div className="cf-app cf-app--loading">
        <div style={{ padding: "40px 20px", textAlign: "center" }}>
          <p style={{ color: "#666" }}>카페 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`cf-app ${isDesktop ? "cf-app--desktop" : ""}`}>
      {!isDesktop && <TopBar />}

      {isDesktop && (
        <SidePanel
          cafe={selectedCafe}
          place={selectedPlace}
          visibleCafes={visibleCafes}
          places={indexPlaces}
          region={region}
          userLocation={userLocation}
          userRegionName={userRegionName}
          selectedPlaceId={selectedPlaceId}
          onSelectCafe={handleSelectCafe}
          onSelectPlace={handleSelectPlace}
          onCloseCafe={handleCloseSheet}
          onClosePlace={handleClosePlace}
          onShare={handleShareCurrent}
        />
      )}

      <RegionChips
        active={region}
        onChange={handleRegionChange}
        cafes={indexCafes}
      />

      <div className="cf-stage">
        <MapView
          cafes={visibleCafes}
          places={indexPlaces}
          selectedCafeId={selectedCafeId}
          selectedPlaceId={selectedPlaceId}
          selectedCafe={selectedCafe}
          userLocation={userLocation}
          region={region}
          isDesktop={isDesktop}
          sheetMode={sheetMode}
          routeMode={routeMode}
          onSelectCafe={handleSelectCafe}
          onSelectPlace={handleSelectPlace}
          onMapBlankClick={handleCloseSheet}
          onMapReady={handleMapReady}
        />

        <MapControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onLocate={handleLocate}
          bottom={mapCtrlsBottom}
          routeMode={routeMode}
          onToggleRoute={handleToggleRoute}
          showRouteToggle={!!selectedCafeId}
        />
      </div>

      {!isDesktop && (
        <Sheet
          open={true}
          mode={sheetMode}
          onModeChange={setSheetMode}
          header={
            selectedCafe || selectedPlace ? (
              <div className="cf-sheet__topbar">
                <button
                  className="cf-sheet__back"
                  onClick={
                    selectedPlace ? handleClosePlace : handleCloseSheet
                  }
                  aria-label={selectedPlace ? "카페 상세로" : "카페 목록으로"}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                <div className="cf-sheet__topbar-spacer" />
                <button
                  className="cf-sheet__share"
                  onClick={handleShareCurrent}
                  aria-label="공유"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <path d="M8.6 13.5l6.9 4M15.5 6.5l-6.9 4" />
                  </svg>
                </button>
              </div>
            ) : null
          }
        >
          {selectedPlace ? (
            <PlaceDetail
              place={selectedPlace}
              fromCafe={selectedCafe}
              userLocation={userLocation}
            />
          ) : selectedCafe ? (
            <CafeDetail
              cafe={selectedCafe}
              places={indexPlaces}
              userLocation={userLocation}
              onSelectPlace={handleSelectPlace}
              selectedPlaceId={selectedPlaceId}
            />
          ) : (
            <CafeList
              cafes={visibleCafes}
              places={indexPlaces}
              region={region}
              userLocation={userLocation}
              userRegionName={userRegionName}
              onSelectCafe={handleSelectCafe}
            />
          )}
        </Sheet>
      )}
    </div>
  );
}
